import { Box, Typography, useTheme } from '@mui/material';
import ListCell from 'components/list/list-cell';
import FormListCell from 'components/list/form-list-cell';
import ListRow from 'components/list/list-row';
import NumberField from 'components/number-field';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import Tooltip from 'components/tooltip';
import { AlertTriangle, Ban, Cylinder, MinusCircle, PlusCircle, Trash2 } from 'lucide-react';
import Mui from 'materials';
import React from 'react';
import { UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { CreateOrderDistributionForm } from 'views/order-feed/features/order-distribution/components/create-order-distribution';
import { OrderDistribution } from 'views/order-feed/features/order-distribution/types/order-distribution.type';
import { DistributedQuantities } from 'views/order-feed/features/order-distribution/types/distributed-quantities.type';

type Props = {
  form: UseFormReturn<CreateOrderDistributionForm>;
  silo: {
    name: string;
    capacity: number;
    id: string;
    isExtraStorage: boolean;
    isEnabled: boolean;
    hasConfigChanged: boolean;
    description: string | null;
  };
  nestedIndex: number;
  distribution: OrderDistribution;
  distributed: DistributedQuantities;
  originalToDistribute: DistributedQuantities;
};

const PAGE_NAME_ID = 'orderFeed.orderDistribution.createDistribution.form';

const DistributeSiloForm: React.FC<Props> = ({ silo, nestedIndex, form, originalToDistribute, distributed, distribution }) => {
  const intl = useIntl();
  const theme = useTheme();
  const { submitCount } = form.formState;
  const {
    fields: rows,
    remove,
    prepend,
  } = useFieldArray({
    control: form.control,
    name: `silos.${nestedIndex}.siloDistributions`,
  });

  const watchDistributions = useWatch({
    control: form.control,
    name: `silos.${nestedIndex}.siloDistributions`,
  });

  const exceedsProductQuantity = (index: number) => {
    const itemNumber = watchDistributions[index]?.itemNumber;

    return distributed[itemNumber] > originalToDistribute[itemNumber];
  };

  const exceedsSiloCapacity = (index: number) => {
    const distributions = form.getValues(`silos.${nestedIndex}.siloDistributions`).filter((d) => !!d.itemNumber);
    if (!distributions.length) return false;

    const distributedQuantity = distributions.reduce((total, current) => total + Number(current.quantity), 0);
    return distributedQuantity > silo.capacity;
  };

  const clearFormProductField = (siloIndex: number, productIndex: number) => {
    form.resetField(`silos.${siloIndex}.siloDistributions.${productIndex}.quantity`, { defaultValue: 0 });
    form.resetField(`silos.${siloIndex}.siloDistributions.${productIndex}.itemNumber`, { defaultValue: '' });
  };

  return rows.map((row, index) => {
    const productFieldName = `silos.${nestedIndex}.siloDistributions.${index}.itemNumber` as const;
    const quantityFieldName = `silos.${nestedIndex}.siloDistributions.${index}.quantity` as const;
    const exceedsQuantity = exceedsProductQuantity(index);
    const exceedsCapacity = exceedsSiloCapacity(index);
    const isSiloEmpty = Number(form.getValues(quantityFieldName)) === 0;
    const isDisabled = silo.hasConfigChanged || !silo.isEnabled;
    const disabledRowColor = silo.isEnabled ? 'transparent' : '#d7d7d796';
    const validateRow = () => {
      if (submitCount === 0) return;

      void form.trigger([productFieldName, quantityFieldName]);
    };
    const warnings = [
      exceedsQuantity && intl.formatMessage({ id: `${PAGE_NAME_ID}.productAlert` }),
      exceedsCapacity && intl.formatMessage({ id: `${PAGE_NAME_ID}.capacityAlert` }),
      silo.hasConfigChanged && intl.formatMessage({ id: `${PAGE_NAME_ID}.configHasChanged` }),
    ].filter((warning): warning is string => Boolean(warning));

    return (
      <ListRow
        key={row.id}
        sx={{
          '&.ListRow-tableRow': {
            backgroundColor: disabledRowColor,
          },
          '&.ListRow-tableRow.MuiTableRow-hover:hover': {
            backgroundColor: disabledRowColor,
          },
        }}>
        <ListCell align="center">
          {warnings.map((title) => (
            <Tooltip key={title} title={title}>
              <AlertTriangle fill={theme.palette.warning.light} color={theme.palette.darkBlue.main} size={18} />
            </Tooltip>
          ))}
        </ListCell>
        <ListCell
          sx={{
            minWidth: 120,
          }}>
          {index === 0 ? (
            <Box>
              <Typography variant="h6">
                {silo.name} <small>{silo.description && `- ${silo.description}`}</small>
              </Typography>
              {silo.isExtraStorage ? (
                <Box display={'flex'} justifyContent={'space-evenly'} alignItems={'center'}>
                  <Cylinder size={14} stroke={theme.palette.primary.main} />
                  <small>{intl.formatMessage({ id: `${PAGE_NAME_ID}.extraStorage` })}</small>
                </Box>
              ) : (
                <small>
                  {intl.formatMessage({ id: `${PAGE_NAME_ID}.maxCapacity` })}: {silo.capacity}
                </small>
              )}
            </Box>
          ) : null}
        </ListCell>
        <FormListCell>
          <Select
            disabled={isDisabled}
            fullWidth
            sx={{
              minWidth: 420,
              borderRadius: 2,
            }}
            dark
            onBlur={validateRow}
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.product` })}
            controller={{
              control: form.control,
              name: productFieldName,
              rules: {
                validate: (value) => {
                  const quantity = Number(form.getValues(quantityFieldName));

                  return value || quantity <= 0 ? true : intl.formatMessage({ id: 'form.rules.required' });
                },
              },
            }}>
            {distribution?.products.map((product) => (
              <RadioSelectItem
                key={product.itemNumber}
                disabled={watchDistributions.map((distribution) => distribution.itemNumber).includes(product.itemNumber)}
                value={product.itemNumber}>
                {product.itemName}
              </RadioSelectItem>
            ))}
          </Select>
        </FormListCell>
        <FormListCell>
          <NumberField
            sx={{
              borderRadius: 2,
            }}
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.quantity` })}
            fullWidth
            dark
            disabled={isDisabled}
            onBlur={validateRow}
            controller={{
              control: form.control,
              name: quantityFieldName,
              rules: {
                max: silo.capacity,
                min: 0,
                validate: {
                  moreThanZeroWhenProductSelected: (value) => {
                    const itemNumber = form.getValues(productFieldName);

                    return !itemNumber || Number(value) > 0 ? true : intl.formatMessage({ id: 'form.rules.moreThanZero' });
                  },
                },
              },
            }}
            endAdornment="kg"
          />
        </FormListCell>
        <ListCell>
          {!silo.isEnabled ? (
            <Box display={'flex'} justifyContent={'center'} width={100}>
              <Tooltip title={<Typography variant="h6">Silo disabled</Typography>}>
                <Ban fill={theme.palette.gray.main} color={theme.palette.gray.dark} size={20} visibility="visible" />
              </Tooltip>
            </Box>
          ) : index === 0 ? (
            <Box display={'flex'}>
              <Mui.IconButton
                color="primary"
                title={intl.formatMessage({ id: `${PAGE_NAME_ID}.addLine` })}
                disabled={watchDistributions?.length >= distribution.products.length}
                onClick={() => {
                  prepend({
                    itemNumber: '',
                    quantity: 0,
                    siloId: silo.id,
                  });
                }}>
                <PlusCircle size={34} strokeWidth={1} />
              </Mui.IconButton>
              <Mui.IconButton
                color="primary"
                title={intl.formatMessage({ id: `general.clearSelection` })}
                disabled={silo.hasConfigChanged || isSiloEmpty}
                onClick={() => {
                  clearFormProductField(nestedIndex, index);
                }}>
                <Trash2 size={34} strokeWidth={1} />
              </Mui.IconButton>
            </Box>
          ) : (
            <Mui.IconButton
              color="primary"
              title={intl.formatMessage({ id: `${PAGE_NAME_ID}.removeLine` })}
              onClick={() => {
                remove(index);
              }}>
              <MinusCircle size={34} strokeWidth={1} />
            </Mui.IconButton>
          )}
        </ListCell>
      </ListRow>
    );
  });
};

export default React.memo(DistributeSiloForm);
